import React, { useState, useEffect, useMemo } from 'react';
import { Link } from "react-router-dom";
import 'bootstrap/dist/css/bootstrap.min.css';
import { usePlantData } from '../../../components/data/PlantData';
import { usePortfolioData } from '../../../components/data/PortfolioData';
import SidebarPageLayout from '../../../components/layout/SidebarPageLayout';
import Marquee from '../../../components/layout/Marquee';
import AlarmsTable from '../../../components/layout/AlarmsTable';
import { apiFetch } from '../../../api/apiClient';
import { toSlug } from '../../../util/strings';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleXmark, faCircleExclamation, faCircleInfo, faCircleQuestion, faCircleCheck, faHeartPulse, faFileLines } from '@fortawesome/free-solid-svg-icons';
import Breadcrumbs from '../../../components/layout/Breadcrumbs';
import { usePortfolioDateFormatter } from '../../../utils/usePortfolioDateFormatter';

function normalizePortfolioKey(value = '') {
  return String(value).toLowerCase().replace(/[^a-z0-9]/g, '');
}

const EMPTY_ALARMS_STATUS = { critical: 0, major: 0, minor: 0, info: 0, unknown: 0 };
const ALARM_WINDOW_DAYS = 7;
const COUNTED_ALARM_STATUSES = new Set(['critical', 'major', 'minor', 'info']);

function parseAlarmRaisedDate(value) {
  const match = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(String(value ?? '').trim());
  if (!match) {
    return null;
  }
  const month = Number(match[1]);
  const day = Number(match[2]);
  const year = Number(match[3]);
  const date = new Date(year, month - 1, day);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return null;
  }
  return date;
}

function calendarDateInTimeZone(now, timeZone) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(now);
  const get = (type) => Number(parts.find((part) => part.type === type)?.value);
  return new Date(get('year'), get('month') - 1, get('day'));
}

function isAlarmWithinWindow(alarm, timeZone, now = new Date()) {
  const raised = parseAlarmRaisedDate(alarm?.date_raised ?? alarm?.dateRaised);
  if (!raised) {
    return false;
  }
  const cutoff = calendarDateInTimeZone(now, timeZone);
  cutoff.setDate(cutoff.getDate() - ALARM_WINDOW_DAYS);
  return raised >= cutoff;
}

function countAlarmStatuses(events) {
  return events.reduce((counts, event) => {
    const status = String(event?.status ?? '').trim().toLowerCase();
    if (COUNTED_ALARM_STATUSES.has(status)) {
      counts[status] += 1;
    } else {
      counts.unknown += 1;
    }
    return counts;
  }, { ...EMPTY_ALARMS_STATUS });
}

function Alarms({ plantName }) {
  const { plant: plantData } = usePlantData();
  const { data: portfolios } = usePortfolioData();
  const [inverterEvents, setInverterEvents] = useState([]);
  const [dcdcConverterEvents, setDcdcConverterEvents] = useState([]);
  const [bessEvents, setBessEvents] = useState([]);
  const [alarmsLastUpdated, setAlarmsLastUpdated] = useState(null);
  const portfolioName = toSlug(plantName) ?? "";
  const dateFormatter = usePortfolioDateFormatter({ portfolioName });
  const kpiLastUpdated = useMemo(() => {
    const normalized = normalizePortfolioKey(portfolioName);
    const match = (portfolios ?? []).find((node) => (
      normalizePortfolioKey(node.portfolio_name) === normalized
      || normalizePortfolioKey(node.plant_name) === normalized
    ));
    return match?.last_updated ?? null;
  }, [portfolios, portfolioName]);
  const lastUpdated = alarmsLastUpdated ?? kpiLastUpdated;
  const recentInverterEvents = useMemo(
    () => inverterEvents.filter((event) => isAlarmWithinWindow(event, dateFormatter.timeZone)),
    [inverterEvents, dateFormatter.timeZone],
  );
  const recentDcdcConverterEvents = useMemo(
    () => dcdcConverterEvents.filter((event) => isAlarmWithinWindow(event, dateFormatter.timeZone)),
    [dcdcConverterEvents, dateFormatter.timeZone],
  );
  const recentBessEvents = useMemo(
    () => bessEvents.filter((event) => isAlarmWithinWindow(event, dateFormatter.timeZone)),
    [bessEvents, dateFormatter.timeZone],
  );
  const alarmsStatus = useMemo(
    () => countAlarmStatuses([
      ...recentInverterEvents,
      ...recentDcdcConverterEvents,
      ...recentBessEvents,
    ]),
    [recentInverterEvents, recentDcdcConverterEvents, recentBessEvents],
  );

  useEffect(() => {
    const titlePlantName = (plantData?.plant_name ?? portfolioName ?? "Plant");
    document.title = `Vivosol Plant Performance Insights Hub | ${titlePlantName} / Monitoring / Alarms`;
  }, [plantData?.plant_name, portfolioName]);

  useEffect(() => {
    let cancelled = false;

    const loadEvents = async (device) => {
      try {
        const raw = await apiFetch(`/api/events/portfolio/${portfolioName}/${device}/all`);
        if (Array.isArray(raw)) {
          return { events: raw, last_updated: null };
        }
        return {
          events: Array.isArray(raw?.events) ? raw.events : [],
          last_updated: raw?.last_updated ?? null,
        };
      } catch (apiError) {
        console.warn(`API fetch failed for /api/events/portfolio/${portfolioName}/${device}/all:`, apiError);
        return { events: [], last_updated: null };
      }
    };

    (async () => {
      const [inverter, dcdcConverter, bess] = await Promise.all([
        loadEvents("inverter"),
        loadEvents("dcdc-converter"),
        loadEvents("bess"),
      ]);

      if (!cancelled) {
        setInverterEvents(inverter.events);
        setDcdcConverterEvents(dcdcConverter.events);
        setBessEvents(bess.events);
        setAlarmsLastUpdated(
          inverter.last_updated ?? dcdcConverter.last_updated ?? bess.last_updated ?? null,
        );
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [portfolioName]);
  
  const onlineStatus = {
    inverters: (plantData.inverters_online ?? 0) + ' / ' + (parseInt(plantData.inverters_online ?? 0) + parseInt(plantData.inverters_offline ?? 0)),
    dcdcConverters: (plantData.dcdc_converters_online ?? 0) + ' / ' + (parseInt(plantData.dcdc_converters_online ?? 0) + parseInt(plantData.dcdc_converters_offline ?? 0)),
    bess: (plantData.battery_racks_online ?? 0) + ' / ' + (parseInt(plantData.battery_racks_online ?? 0) + parseInt(plantData.battery_racks_offline ?? 0))
  };

  return (
    <SidebarPageLayout id="alarms">
      <div className="content p-5">
        <Marquee />
        <div className="d-flex justify-content-between align-items-start gap-3 mt-3">
          <div>
            <h1 className="mb-0"><Link to={`${plantData.dashboard}`}><strong>{plantData.plant_name}</strong></Link> : {plantData.config} ({plantData.location})</h1>
            <Breadcrumbs labels={{ portfolioName: plantData.plant_name }} />
          </div>
          <div className="text-end text-nowrap small pt-1">
            {lastUpdated ? (
              <div title="Alarm events last updated (hourly pull)">
                <strong>Last Updated:</strong> {dateFormatter.formatUtc(lastUpdated)}
              </div>
            ) : null}
            <div className="text-secondary">Alarms limited to the last 7 days</div>
          </div>
        </div>
        <div className="metrics mb-5">
          <div className="card critical">
            <span className="title">
              <span>CRITICAL</span><FontAwesomeIcon icon={faCircleXmark} />
            </span>
            <div className="metric">
              {alarmsStatus.critical}
            </div>
          </div>
          <div className="card major">
            <span className="title">
              <span>MAJOR</span><FontAwesomeIcon icon={faCircleExclamation} />
            </span>
            <div className="metric">
              {alarmsStatus.major}
            </div>
          </div>
          <div className="card minor">
            <span className="title">
              <span>MINOR</span><FontAwesomeIcon icon={faCircleCheck} />
            </span>
            <div className="metric">
              {alarmsStatus.minor}
            </div>
          </div>
          <div className="card event">
            <span className="title">
              <span>EVENT</span><FontAwesomeIcon icon={faCircleInfo} />
            </span>
            <div className="metric">
              {alarmsStatus.info}
            </div>
          </div>
          <div className="card unknown">
            <span className="title">
              <span>UNKNOWN</span><FontAwesomeIcon icon={faCircleQuestion} />
            </span>
            <div className="metric">
              {alarmsStatus.unknown}
            </div>
          </div>
        </div>

        <div id="inverteralarms" className="panel p-3 mb-5">
          <div className="header d-flex justify-content-between pb-3">
            <div className="left d-flex gap-3">
              <span><FontAwesomeIcon icon={faHeartPulse} /> INVERTER ALARMS</span> - <span className="online">(<strong>INVERTERS ONLINE</strong>: {onlineStatus.inverters})</span>
            </div>
            <div className="right d-flex align-items-stretch gap-2">
              <Link to="/help/documentation/fault-codes#inverter" aria-label="Inverter fault code glossary">
                <FontAwesomeIcon icon={faFileLines} />
              </Link>
            </div>
          </div>
          <div>
            <AlarmsTable data={recentInverterEvents} />
          </div>
        </div>

        <div id="inverteralarms" className="panel p-3 mb-5">
          <div className="header d-flex justify-content-between pb-3">
            <div className="left d-flex gap-3">
              <span><FontAwesomeIcon icon={faHeartPulse} /> DCDC CONVERTER ALARMS</span> - <span className="online">(<strong>DCDC's ONLINE</strong>: {onlineStatus.dcdcConverters})</span>
            </div>
            <div className="right d-flex align-items-stretch gap-2">
              <Link to="/help/documentation/fault-codes#dcdc-converter" aria-label="DC-DC converter fault code glossary">
                <FontAwesomeIcon icon={faFileLines} />
              </Link>
            </div>
          </div>
          <div>
            <AlarmsTable data={recentDcdcConverterEvents} />
          </div>
        </div>

        <div id="bessalarms" className="panel p-3 mb-5">
          <div className="header d-flex justify-content-between pb-3">
            <div className="left d-flex gap-3">
              <span><FontAwesomeIcon icon={faHeartPulse} /> BESS ALARMS</span> - <span className="online">(<strong>BESS UNITS ONLINE</strong>: {onlineStatus.bess})</span>
            </div>
            <div className="right d-flex align-items-stretch gap-2">
              <Link to="/help/documentation/fault-codes#bess" aria-label="BESS fault code glossary">
                <FontAwesomeIcon icon={faFileLines} />
              </Link>
            </div>
          </div>
          <div>
            <AlarmsTable data={recentBessEvents} />
          </div>
        </div>

      </div>
    </SidebarPageLayout>
  );
}

export default Alarms;
